import { useMemo } from "react";
import getConfig from "next/config";
import type { GetServerSidePropsContext } from "next";
import type { NormalizedCacheObject } from "@apollo/client";
import { ApolloClient, ApolloLink, from, HttpLink, InMemoryCache } from "@apollo/client";
import { sha256 } from "crypto-hash";
import { createPersistedQueryLink } from "@apollo/client/link/persisted-queries";
import customFetch from "./services/utils/apolloCustomFetch";
import { iHeaders } from "./services/queries/interfaces/headers";

export type ApolloClientType = ApolloClient<NormalizedCacheObject>;

const DEFAULT_LOCALE = "en";

let apolloClient: ApolloClient<NormalizedCacheObject>;

const createApolloUri = (locale: string, topLevelDomain: string): string => {
	const { publicRuntimeConfig } = getConfig();
	const uriLocale: string = locale || DEFAULT_LOCALE;
	const clientSideBase = process.env.NEXT_PUBLIC_LOCAL_BASE || "";
	const serverSideBase = publicRuntimeConfig?.drupal[topLevelDomain]?.gql || publicRuntimeConfig?.drupal?.com.gql;
	const base = typeof window === "undefined" ? serverSideBase : clientSideBase;

	return `${base}/${publicRuntimeConfig.subdir}/${uriLocale}/graphql`;
};

const createApolloClient = (
	locale?: string,
	topLevelDomain?: string,
	ctx?: GetServerSidePropsContext | null,
	cookies?: string | null,
	getReq?: boolean,
) => {
	const isGetGraphQl = process.env.NEXT_PUBLIC_GET_GRAPHQL || false;

	const httpLink = new HttpLink({
		uri: createApolloUri(locale || "en", topLevelDomain || "com"),
		credentials: "include",
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		fetch: customFetch,
		fetchOptions: {
			//mode: "no-cors",
			cookie: cookies,
		},
		headers: { cookie: cookies || "", "Apollo-Require-Preflight": true },
	});

	const afterWareLink = new ApolloLink((operation, forward) =>
		forward(operation).map((response: any) => {
			const context = operation.getContext();

			const headers: iHeaders = {
				status: context.response.status,
				statusText: context.response.statusText,
			};
			if (response.data) {
				context.response.headers.forEach((perHeaderValue: string, perHeaderName: string) => {
					headers[perHeaderName] = perHeaderValue;
				});

				response.data.pageMetaInformation = {
					PurgeCacheTags: context.response.headers.get("Purge-Cache-Tags") || "",
					headers,
				};

				Object.keys(response.data).forEach((queryName: string) => {
					if (response.data[queryName]?.feTempStorage === null) {
						response.data[queryName].feTempStorage = {
							__headers: headers,
						};
					}
				});
			}
			return response;
		}),
	);

	const persistedQueryLink = createPersistedQueryLink({
		sha256,
		useGETForHashedQueries: true,
	}).concat(httpLink);

	return new ApolloClient({
		ssrMode: typeof window === "undefined",
		link:
			getReq && isGetGraphQl === "true" ? from([afterWareLink, persistedQueryLink]) : from([afterWareLink, httpLink]),
		cache: new InMemoryCache(),
		defaultOptions: {
			watchQuery: {
				errorPolicy: "all",
				fetchPolicy: "no-cache",
			},
		},
	});
};

export function initializeApollo(
	locale = "en",
	topLevelDomain = "",
	ctx = null,
	cookies: string | null = null,
	getReq?: boolean,
): ApolloClient<NormalizedCacheObject> {
	return apolloClient ?? createApolloClient(locale, topLevelDomain, ctx, cookies, getReq);
}

export function useApollo(language: string, tld: string, cookies: string) {
	return useMemo(() => initializeApollo(language, tld, null, cookies), [language, tld, cookies]);
}
